import React, { useMemo, useCallback, useState, useRef, useEffect, useContext } from 'react';
import { Cascader } from 'antd';
import type { CascaderProps as AntCascaderProps } from 'antd';
import { useAndStore } from '../../base/store/useAndStore';
import { getDataSetStoreKey } from '../../base/storeKeyUtils';
import { parseChangeAction, createCommand } from '../../base/actionParser';
import { useFieldValidation } from '../../base/useFieldValidation';
import { request as coreRequest } from '../../config';
import { ValidateHelp } from '../../base/ValidateHelp';
import type { BaseComponentProps, DataSetConfig, ValidationDisplayConfig } from '../../types/common';
import { DataSetInheritContext, useResolvedDataSet } from '../../base/DataSetInheritContext';
import { deriveScopedComponentId } from '../../base/storePropsUtils';

const VALIDATE_DEBOUNCE_MS = 200;

// 选项配置接口
interface OptionsCfg {
  /** 远程数据 URL */
  url?: string;
  /** 值字段名 */
  codeKey?: string;
  /** 标签字段名 */
  labelKey?: string;
  /** 子节点字段名 */
  childrenKey?: string;
  /** 最大层级 */
  maxLevel?: number;
}

export type AndCascadeDisplayRenderMode = 'default' | 'full' | 'last';

/**
 * AndCascade Props 接口
 */
export interface AndCascadeProps extends Omit<AntCascaderProps, 'onChange'>, BaseComponentProps, DataSetConfig, ValidationDisplayConfig {
  /** 远程选项配置 */
  optionsCfg?: OptionsCfg;
  /** 校验规则 */
  rules?: any[];
  /** 失焦时校验 */
  validateOnBlur?: boolean;
  /** 修改时校验 */
  validateOnChange?: boolean;
  /** 变更动作 */
  changeAction?: string;
  /** 选中后输入框展示：default | full | last */
  displayRenderMode?: AndCascadeDisplayRenderMode;
  /** 自定义变更处理函数 */
  onChange?: (value: any, selectedOptions?: any[]) => void;
  /** 远程数据URL（自定义属性，不传递给Cascader） */
  remoteUrl?: string;
  /** 迭代器行下标（与 DataSetInheritContext 互补） */
  index?: number;
}

function resolveCascadeDisplayRender(
  mode: AndCascadeDisplayRenderMode | undefined,
  displayRender: AntCascaderProps['displayRender'],
): AntCascaderProps['displayRender'] {
  if (typeof displayRender === 'function') return displayRender;
  switch (mode) {
    case 'full':
      return (labels) => labels.join(' / ');
    case 'last':
      return (labels) => labels[labels.length - 1] ?? '';
    default:
      return undefined;
  }
}

/**
 * 递归转换数据为级联选择器格式
 */
const transformToCascaderOptions = (
  nodes: any[],
  cfg: { codeKey: string; labelKey: string; childrenKey: string; maxLevel: number },
  currentLevel: number = 1
): any[] => {
  if (!Array.isArray(nodes)) return [];
  const { codeKey, labelKey, childrenKey, maxLevel } = cfg;

  return nodes.map((node: any) => {
    const children = (currentLevel < maxLevel && Array.isArray(node?.[childrenKey]))
      ? transformToCascaderOptions(node[childrenKey], cfg, currentLevel + 1)
      : undefined;

    const item: any = {
      value: String(node?.[codeKey]),
      label: node?.[labelKey],
    };
    if (children && children.length) item.children = children;
    return item;
  });
};

/**
 * 将字符串转换为数组
 */
const stringToArray = (str: string): string[] => {
  if (!str || typeof str !== 'string') return [];
  return str.includes(',') ? str.split(',').map(s => s.trim()).filter(s => s) : [str];
};

/**
 * 在级联选项中查找路径
 */
const findPathInOptions = (targetValue: string, options: any[]): string[] => {
  const findPath = (nodes: any[], target: string, path: string[] = []): string[] | null => {
    for (const node of nodes) {
      const nodeValue = node.value;
      const currentPath = [...path, nodeValue];

      if (nodeValue === target) {
        return currentPath;
      }
      if (node.children && Array.isArray(node.children)) {
        const result = findPath(node.children, target, currentPath);
        if (result) return result;
      }
    }
    return null;
  };

  return findPath(options, targetValue) || [];
};

/**
 * 判断是否一维数组
 */
const is1DimensionalArray = (arr: any): boolean => {
  return Array.isArray(arr) && arr.length > 0 && !Array.isArray(arr[0]);
};

/**
 * 判断是否二维数组
 */
const is2DimensionalArray = (arr: any): boolean => {
  return Array.isArray(arr) && arr.length > 0 && Array.isArray(arr[0]);
};

/**
 * AndCascade 组件
 * 封装 Ant Design Cascader，支持远程数据和动作解析
 */
export const AndCascade: React.FC<AndCascadeProps> = ({
  id,
  sessionId,
  develop,
  dataSet,
  dataSetField,
  optionsCfg,
  rules,
  validateOnBlur,
  validateOnChange,
  showValidateHelp = true,
  changeAction,
  displayRenderMode,
  onChange,
  options: propOptions,
  defaultValue,
  value: propValue,
  remoteUrl: _remoteUrl,
  displayRender,
  index,
  ...restProps
}) => {
  const resolved = useResolvedDataSet(dataSet, dataSetField, sessionId);
  const effectiveDataSet = resolved.dataSet;
  const effectiveDataSetField = resolved.dataSetField;
  const effectiveSessionId = resolved.sessionId;
  const ctx = useContext(DataSetInheritContext);
  const effectiveIndex = index ?? ctx?.index;
  const effectiveParentIndex = ctx?.parentIndex;

  const addCommand = useAndStore((s) => s.addCommand);
  const setDataSet = useAndStore((s) => s.setDataSet);
  const validateTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const derivedPropsComponentId = useMemo(
    () => (effectiveSessionId ? deriveScopedComponentId(id, ctx, index) : null),
    [effectiveSessionId, id, ctx?.parentIndex, ctx?.index, index]
  );
  const storeProps = useAndStore((s) => {
    if (!effectiveSessionId) return null;
    const bucket = s.propsStore[`${effectiveSessionId}_props`];
    if (!bucket) return null;
    if (derivedPropsComponentId && bucket[derivedPropsComponentId]) {
      return bucket[derivedPropsComponentId];
    }
    if (id && bucket[id]) return bucket[id];
    return null;
  });
  const effectiveDisplayRenderMode = useMemo(
    () => (storeProps?.displayRenderMode as AndCascadeDisplayRenderMode | undefined) ?? displayRenderMode,
    [storeProps?.displayRenderMode, displayRenderMode],
  );
  const effectiveChangeAction = useMemo(
    () => (typeof storeProps?.changeAction === 'string' ? storeProps.changeAction : changeAction),
    [storeProps?.changeAction, changeAction],
  );
  const effectiveOptionsCfg = useMemo(
    () => (storeProps?.optionsCfg != null ? storeProps.optionsCfg : optionsCfg),
    [storeProps?.optionsCfg, optionsCfg],
  );
  const resolvedDisplayRender = useMemo(
    () => resolveCascadeDisplayRender(effectiveDisplayRenderMode, displayRender),
    [effectiveDisplayRenderMode, displayRender],
  );

  const [remoteOptions, setRemoteOptions] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const dataSetKey = useMemo(
    () => getDataSetStoreKey(effectiveSessionId, effectiveDataSet),
    [effectiveSessionId, effectiveDataSet]
  );
  const storeValue = useAndStore((s) =>
    dataSetKey && effectiveDataSetField ? s.getDataSet(dataSetKey, effectiveDataSetField) : undefined
  );

  const { validate, help, validateStatus } = useFieldValidation({
    rules,
    dataSet: effectiveDataSet,
    dataSetField: effectiveDataSetField,
    sessionId: effectiveSessionId,
    id,
    develop,
  });

  // 是否有有效的远程配置
  const hasValidRemoteCfg = useMemo(() => {
    return effectiveOptionsCfg?.url && remoteOptions.length > 0;
  }, [effectiveOptionsCfg?.url, remoteOptions.length]);

  // 计算最终显示的值
  const displayValue = useMemo(() => {
    // 获取原始值
    let rawValue: any = storeValue ?? propValue ?? defaultValue ?? [];

    // 处理字符串类型
    if (typeof rawValue === 'string') {
      if (hasValidRemoteCfg) {
        const result = stringToArray(rawValue).map(item => findPathInOptions(item, remoteOptions));
        return result.filter(item => Array.isArray(item) && item.length > 0);
      }
      return [rawValue];
    }

    // 处理一维数组（多选存 id 列表时须还原为路径）
    if (is1DimensionalArray(rawValue) && hasValidRemoteCfg) {
      const result = rawValue.map((item: string) => {
        const path = findPathInOptions(String(item), remoteOptions);
        return path.length > 0 ? path : stringToArray(item);
      });
      return result.filter((item: any) => Array.isArray(item) && item.length > 0);
    }

    // 处理二维数组
    if (is2DimensionalArray(rawValue) && hasValidRemoteCfg) {
      return rawValue.filter((item: any) => Array.isArray(item) && item.length > 0);
    }

    return rawValue;
  }, [storeValue, propValue, defaultValue, hasValidRemoteCfg, remoteOptions]);

  // 计算最终选项
  const finalOptions = useMemo(() => {
    // 优先级: 远程选项 > 配置的选项
    if (effectiveOptionsCfg?.url && remoteOptions.length > 0) {
      return remoteOptions;
    }
    return propOptions || [];
  }, [effectiveOptionsCfg?.url, remoteOptions, propOptions]);

  // 加载远程选项
  useEffect(() => {
    const loadRemoteOptions = async () => {
      if (!effectiveOptionsCfg?.url) return;

      setIsLoading(true);
      try {
        // 使用注入的 request 方法
        const data = await coreRequest({ url: effectiveOptionsCfg.url, method: 'GET' });
        const sourceArray = Array.isArray(data)
          ? data
          : (data?.data ? (Array.isArray(data.data) ? data.data : data.data.list || data.data.records || []) : []);

        const codeKey = effectiveOptionsCfg.codeKey || 'value';
        const labelKey = effectiveOptionsCfg.labelKey || 'label';
        const childrenKey = effectiveOptionsCfg.childrenKey || 'children';
        const maxLevel = effectiveOptionsCfg.maxLevel || 5;

        const transformed = transformToCascaderOptions(sourceArray, {
          codeKey,
          labelKey,
          childrenKey,
          maxLevel,
        });

        setRemoteOptions(transformed);
      } catch (error) {
        console.error('Failed to load remote options:', error);
        setRemoteOptions([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadRemoteOptions();
  }, [
    effectiveOptionsCfg?.url,
    effectiveOptionsCfg?.codeKey,
    effectiveOptionsCfg?.labelKey,
    effectiveOptionsCfg?.childrenKey,
    effectiveOptionsCfg?.maxLevel,
  ]);

  const handleChange = useCallback(
    (value: any, selectedOptions?: any[]) => {
      if (onChange) {
        onChange(value, selectedOptions);
        return;
      }
      let processedValue: any = value;
      if (is1DimensionalArray(value) && hasValidRemoteCfg) {
        processedValue = String(value[value.length - 1] || '');
      } else if (is2DimensionalArray(value) && hasValidRemoteCfg) {
        processedValue = value.map((item: any[]) => item.join(','));
      }
      if (dataSetKey && effectiveDataSetField) {
        setDataSet(dataSetKey, effectiveDataSetField, processedValue);
      }
      if (validateOnChange) {
        if (validateTimerRef.current) clearTimeout(validateTimerRef.current);
        validateTimerRef.current = setTimeout(() => {
          validateTimerRef.current = null;
          validate(value);
        }, VALIDATE_DEBOUNCE_MS);
      }
      if (effectiveChangeAction && effectiveSessionId) {
        const { actionName, arg } = parseChangeAction(effectiveChangeAction, processedValue, {
          id,
          dataSet: effectiveDataSet,
          dataSetField: effectiveDataSetField,
          index: effectiveIndex,
          parentIndex: effectiveParentIndex,
        }, ctx);
        addCommand(effectiveSessionId, createCommand(actionName, arg));
      }
    },
    [
      onChange,
      dataSetKey,
      effectiveDataSetField,
      setDataSet,
      validateOnChange,
      validate,
      effectiveChangeAction,
      effectiveSessionId,
      id,
      effectiveDataSet,
      effectiveIndex,
      effectiveParentIndex,
      addCommand,
      hasValidRemoteCfg,
      ctx,
    ]
  );

  const handleBlur = useCallback(() => {
    if (!validateOnBlur) return;
    validate(displayValue);
  }, [validateOnBlur, validate, displayValue]);

  useEffect(
    () => () => {
      if (validateTimerRef.current) clearTimeout(validateTimerRef.current);
    },
    []
  );

  return (
    <div style={{ width: '100%' }}>
      <Cascader
        {...(restProps as any)}
        disabled={develop ? false : restProps.disabled}
        open={develop ? false : undefined}
        value={displayValue}
        options={finalOptions}
        displayRender={resolvedDisplayRender}
        onChange={handleChange}
        onBlur={handleBlur}
        loading={isLoading}
        status={validateStatus}
        style={{ width: '100%', ...restProps.style }}
      />
      <ValidateHelp help={help} showValidateHelp={showValidateHelp} />
    </div>
  );
};

export default AndCascade;
